import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlusIcon, AtomIcon, ArrowRightIcon, TrashIcon, CheckCircleIcon, WarningCircleIcon, XCircleIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { listFormulations, deleteFormulation } from "@/lib/api";

function ComplianceBadge({ summary }) {
  if (!summary) return null;
  if (summary.errors > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--danger)]" data-testid="compliance-error">
        <XCircleIcon size={14} weight="fill" /> {summary.errors} error{summary.errors > 1 ? "s" : ""}
      </span>
    );
  }
  if (summary.warnings > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em]" style={{ color: "#B38600" }} data-testid="compliance-warning">
        <WarningCircleIcon size={14} weight="fill" /> {summary.warnings} warning{summary.warnings > 1 ? "s" : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em]" style={{ color: "var(--success)" }} data-testid="compliance-ok">
      <CheckCircleIcon size={14} weight="fill" /> Compliant
    </span>
  );
}

export default function Formulations() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const load = () => {
    setLoading(true);
    listFormulations(q ? { q } : {}).then(setItems).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const handleDelete = async (id) => {
    await deleteFormulation(id);
    toast.success("Formulation deleted");
    load();
  };

  return (
    <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 fade-in" data-testid="formulations-page">
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 border border-[var(--border)] p-8 md:p-10 bg-white mb-6">
        <div className="lg:col-span-8 flex flex-col justify-center">
          <span className="label-kicker">Formulation Builder</span>
          <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tighter mt-3">
            Live compliance checker<br />for every recipe.
          </h1>
          <p className="mt-4 text-sm md:text-base text-[var(--text-muted)] max-w-xl leading-relaxed">
            Drop catalogued ingredients into a formulation, set target %s, and get instant flags
            for prohibited entries, over-limit concentrations and region-specific restrictions.
          </p>
          <div className="flex items-center gap-2 mt-6">
            {hasRole("editor") && (
              <Button onClick={() => navigate("/formulations/new")} className="btn-primary rounded-none h-10 px-5 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="new-formulation-btn">
                New Formulation <ArrowRightIcon size={14} weight="bold" className="ml-2" />
              </Button>
            )}
          </div>
        </div>
        <div className="lg:col-span-4 flex items-center justify-center text-[var(--primary)]">
          <AtomIcon size={140} weight="thin" />
        </div>
      </section>

      <section className="border border-[var(--border)] bg-white">
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search formulations…" className="rounded-none h-9 font-mono text-sm max-w-md" data-testid="search-formulations" />
        </div>

        {loading ? (
          <div className="p-10 text-center font-mono text-xs text-[var(--text-muted)]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center" data-testid="formulations-empty">
            <AtomIcon size={32} weight="light" className="mx-auto text-[var(--text-muted)]" />
            <div className="mt-3 font-mono text-xs text-[var(--text-muted)]">No formulations yet. Create your first recipe.</div>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="formulations-table">
            <thead>
              <tr>
                {["NAME", "REGION", "INGREDIENTS", "TOTAL %", "COMPLIANCE", ""].map((h, i) => (
                  <th key={i} className="label-kicker text-left px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} className="data-row cell-border" data-testid={`form-row-${f.id}`}>
                  <td className="px-4 py-3 font-heading font-medium tracking-tight">
                    <Link to={`/formulations/${f.id}`} className="hover:text-[var(--primary)]">{f.name}</Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{f.region}</td>
                  <td className="px-4 py-3 font-mono text-xs">{(f.ingredients || []).length}</td>
                  <td className="px-4 py-3 font-mono text-xs">{f.total_percent?.toFixed?.(2) ?? "0"}%</td>
                  <td className="px-4 py-3"><ComplianceBadge summary={f.summary} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      <Link to={`/formulations/${f.id}`} className="text-[var(--primary)] text-xs font-mono uppercase tracking-[0.14em] hover:underline" data-testid={`form-open-${f.id}`}>
                        Open →
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="text-[var(--text-muted)] hover:text-[var(--danger)]" data-testid={`form-delete-${f.id}`}>
                            <TrashIcon size={14} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-none">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete formulation?</AlertDialogTitle>
                            <AlertDialogDescription>“{f.name}” will be permanently removed.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(f.id)} className="rounded-none bg-[var(--danger)] hover:bg-[var(--danger)]/90">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}

export { ComplianceBadge };
